/**
 * SIGNUP SOURCE TRACKING
 *
 * Captures referrer + UTM params + landing page on first visit, stashes them
 * in localStorage, then writes them onto the user's profile the first time
 * they sign in (only if not already set).
 */
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'listbright_signup_source_v1';

export interface SignupSourceData {
  signup_source: string | null;
  signup_referrer: string | null;
  signup_landing_page: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  captured_at: string;
}

function classifyReferrer(referrer: string, utmSource: string | null): string {
  if (utmSource) return utmSource.toLowerCase();
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
    if (host.includes('google.')) return 'google';
    if (host.includes('bing.')) return 'bing';
    if (host.includes('duckduckgo.')) return 'duckduckgo';
    if (host.includes('yahoo.')) return 'yahoo';
    if (host.includes('facebook.') || host === 'fb.com' || host.includes('fb.me')) return 'facebook';
    if (host.includes('instagram.')) return 'instagram';
    if (host.includes('linkedin.') || host.includes('lnkd.in')) return 'linkedin';
    if (host.includes('twitter.') || host === 'x.com' || host.includes('t.co')) return 'twitter';
    if (host.includes('tiktok.')) return 'tiktok';
    if (host.includes('youtube.') || host.includes('youtu.be')) return 'youtube';
    if (host.includes('reddit.')) return 'reddit';
    if (host.includes('chatgpt.') || host.includes('openai.')) return 'chatgpt';
    if (host.includes('perplexity.')) return 'perplexity';
    if (host.includes('claude.')) return 'claude';
    if (host.includes('listbright')) return 'internal';
    return host;
  } catch {
    return 'unknown';
  }
}

/**
 * Call once on app boot. Only captures if nothing is stored yet, so the
 * very first landing wins (the real entry point).
 */
export function captureSignupSource() {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const utmSource = params.get('utm_source');
    const referrer = document.referrer || '';

    // Skip capture when navigating internally (referrer is our own domain
    // and there are no UTM params). This prevents internal nav from being
    // recorded as the source.
    if (referrer) {
      try {
        const refHost = new URL(referrer).hostname;
        if (refHost === window.location.hostname && !utmSource) {
          return;
        }
      } catch {
        /* ignore */
      }
    }

    const data: SignupSourceData = {
      signup_source: classifyReferrer(referrer, utmSource),
      signup_referrer: referrer || null,
      signup_landing_page: url.pathname + url.search,
      utm_source: utmSource,
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content'),
      captured_at: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[signupSource] capture failed', e);
  }
}

/**
 * Write the captured source onto the user's profile if not already set.
 * Safe to call on every sign-in — only writes once per user.
 */
export async function persistSignupSourceForUser(userId: string) {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as SignupSourceData;

    // Only update if profile has no source yet
    const { data: profile } = await supabase
      .from('profiles')
      .select('signup_source, signup_source_captured_at')
      .eq('id', userId)
      .maybeSingle();

    if (!profile || profile.signup_source || profile.signup_source_captured_at) {
      // Already recorded — clear local so we don't keep checking
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        signup_source: data.signup_source,
        signup_referrer: data.signup_referrer,
        signup_landing_page: data.signup_landing_page,
        utm_source: data.utm_source,
        utm_medium: data.utm_medium,
        utm_campaign: data.utm_campaign,
        utm_term: data.utm_term,
        utm_content: data.utm_content,
        signup_source_captured_at: data.captured_at,
      })
      .eq('id', userId);

    if (!error) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    console.warn('[signupSource] persist failed', e);
  }
}